/**
 * Product Form
 *
 * - Syncs option radio changes to the hidden select[name="id"] (variant ID).
 * - Disables the submit button when the selected variant is unavailable.
 * - AJAX-submits the form and fires the same cart events as quick-add.js:
 *     cart:count:updated  { count, total }  → badge + shipping progress
 *     cart:item:added                        → cart panel opens + refreshes
 */

import { addWorking, removeWorking } from './utils.js';

const form = document.querySelector('.lp-product-form__form');
if (!form) throw new Error('Product form not found');

const variantDataEl = document.getElementById('product-variants-data');
if (!variantDataEl) throw new Error('product-variants-data element not found');

const variants = JSON.parse(variantDataEl.textContent);
const optionContainer = form.querySelector('.product__options');
const variantSelect = form.querySelector('select[name="id"]');
const submitButton = form.querySelector('.lp-product-form__submit');

// ---------------------------------------------------------------------------
// Variant resolution — mirrors getSelectedVariant() in quick-add.js
// ---------------------------------------------------------------------------

/**
 * Find the variant matching the currently checked option radios.
 * @returns {Object|null} Shopify variant object, or null if no match
 */
function getSelectedVariant() {
    if (!optionContainer) {
        return variants.find(variant => variant.id === Number(variantSelect.value)) || null;
    }
    const fieldsets = [...optionContainer.querySelectorAll('[data-option-position]')];
    // if we don't have fieldsets, then this product has no options and only one variant — return it
    if (fieldsets.length === 0) return variants[0] || null;
    const selectedOptions = fieldsets.map(fieldset => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
    });

    if (selectedOptions.some(o => o === null)) return null;

    return variants.find(variant =>
        variant.options.every((option, index) => option === selectedOptions[index])
    ) || null;
}

// ---------------------------------------------------------------------------
// Update select + submit state after option change
// ---------------------------------------------------------------------------

/** Sync the hidden variant select and submit button to the currently checked options. */
function updateVariantState() {
    const variant = getSelectedVariant();
    if (variant) {
        variantSelect.value = variant.id;
        submitButton.disabled = !variant.available;
        submitButton.textContent = variant.available ? 'Add to cart' : 'Sold out';
    } else {
        variantSelect.value = '';
        submitButton.disabled = true;
        submitButton.textContent = 'Unavailable';
    }
}


// ---------------------------------------------------------------------------
// Add to cart — mirrors addToCart() in quick-add.js
// ---------------------------------------------------------------------------

/**
 * POST the selected variant to the Cart API and fire cart update events.
 * @param {number|string} variantId - Shopify variant ID
 */
async function addToCart(variantId) {
    addWorking(form);
    const startingText = submitButton.textContent;
    submitButton.textContent = 'Adding...';
    const requestBody = {
        id: variantId,
        quantity: parseInt(form.querySelector('[name="quantity"]')?.value || 1)
    };
    const purchaseType = form.querySelector('[name="purchaseType"]:checked')?.value;
    if (purchaseType === 'purchaseTypeSubscription') {
        requestBody.selling_plan = form.querySelector('[name="stayai_selling_plan"]')?.value;
    }
    try {
        const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
        });
        if (!res.ok) throw new Error('Cart add failed');
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        document.dispatchEvent(new CustomEvent('cart:count:updated', {
            detail: { count: cart.item_count, total: cart.total_price }
        }));
        document.dispatchEvent(new CustomEvent('cart:item:added'));
    } finally {
        removeWorking(form);
        submitButton.textContent = startingText
    }
}

// ---------------------------------------------------------------------------
// Event listeners
// ---------------------------------------------------------------------------

form.addEventListener('click', e => {
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (!qtyBtn) return;
    e.preventDefault();
    const delta = parseInt(qtyBtn.dataset.qtyChange, 10);
    const qtyInput = form.querySelector('.quantity-input__value');
    if (!qtyInput) return;
    const current = parseInt(qtyInput.value, 10) || 1;
    qtyInput.value = Math.max(1, current + delta);
});

optionContainer?.addEventListener('change', updateVariantState);

form.addEventListener('submit', async e => {
    e.preventDefault();
    const variantId = variantSelect.value;
    if (!variantId) return;
    await addToCart(variantId);
});

// Set initial state on load (Liquid pre-selects checked radios, so this
// ensures the select and button reflect the server-rendered selection)
updateVariantState();
